import React from 'react';
import {
    ArrowLeft, Shield, User, Database, Scale, Users, Clock,
    UserCheck, Cookie, Lock, RefreshCw, Mail, Eye, Globe
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MarketingShell from './MarketingShell';

interface PrivacyPolicyProps {
    onBack?: () => void;
}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onBack }) => {
    const { t } = useTranslation('legal');

    const collectItems = t('privacy_policy.data_collection.items', { returnObjects: true }) as string[];
    const purposeItems = t('privacy_policy.purpose.items', { returnObjects: true }) as string[];
    const legalBasisItems = t('privacy_policy.legal_basis.items', { returnObjects: true }) as string[];
    const sharingItems = t('privacy_policy.data_sharing.items', { returnObjects: true }) as string[];
    const transferItems = t('privacy_policy.international_transfers.items', { returnObjects: true }) as string[];
    const retentionItems = t('privacy_policy.data_retention.items', { returnObjects: true }) as string[];
    const rightsItems = t('privacy_policy.your_rights.items', { returnObjects: true }) as string[];

    const card = 'bg-hig-bg-tertiary/20 backdrop-blur-sm border border-hig-border rounded-2xl p-6';
    const cardAccent = 'bg-hig-bg-tertiary/20 backdrop-blur-sm border border-hig-border rounded-2xl p-6 border-l-4 border-l-hig-accent';
    const inner = 'bg-hig-bg-tertiary/30 rounded-xl p-4';
    const h2 = 'text-lg font-semibold text-hig-text-primary mb-4 flex items-center gap-2';
    const body = 'text-hig-text-secondary leading-relaxed';
    const listClass = 'list-disc list-inside text-hig-text-secondary space-y-2';

    return (
        <MarketingShell>
            {/* Page header */}
            <div className="border-b border-hig-border bg-hig-bg-secondary/30">
                <div className="max-w-4xl mx-auto px-6 pt-10 pb-10">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="inline-flex items-center gap-2 text-hig-text-tertiary hover:text-hig-text-primary text-sm mb-8 transition-colors"
                        >
                            <ArrowLeft size={15} />
                            {t('privacy_policy.back', 'Back')}
                        </button>
                    )}
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#0A84FF]/10 flex items-center justify-center flex-shrink-0">
                            <Shield size={22} className="text-[#0A84FF]" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-hig-text-primary">{t('privacy_policy.title')}</h1>
                            <p className="text-hig-text-tertiary text-sm mt-0.5">{t('privacy_policy.last_updated')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-12">
                <div className="space-y-6">

                    {/* Pre-Launch Legal Notice */}
                    {t('legal_notice.title', { defaultValue: '' }) && (
                        <section className="bg-hig-accent/10 border border-hig-accent/20 rounded-2xl p-6 mb-8">
                            <h2 className="text-lg font-semibold text-hig-accent mb-4 flex items-center gap-2">
                                <Shield size={20} />
                                {t('legal_notice.title')}
                            </h2>
                            <p className={body}>{t('legal_notice.description')}</p>
                            
                            {t('legal_notice.transition_clause', { defaultValue: '' }) && (
                                <p className="text-sm text-hig-text-tertiary italic mt-3">
                                    {t('legal_notice.transition_clause')}
                                </p>
                            )}
                        </section>
                    )}

                    {/* 1. Introduction */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Shield size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.introduction.title')}
                        </h2>
                        <p className={`${body} mb-4`}>{t('privacy_policy.introduction.p1')}</p>
                        <p className={body}>{t('privacy_policy.introduction.p2')}</p>
                    </section>

                    {/* 2. Controller */}
                    <section className={card}>
                        <h2 className={h2}>
                            <User size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.controller.title')}
                        </h2>
                        {t('privacy_policy.controller.description') && (
                            <p className={`${body} mb-4`}>{t('privacy_policy.controller.description')}</p>
                        )}
                        <div className={inner}>
                            <p className="text-hig-text-primary font-medium">{t('privacy_policy.controller.name')}</p>
                            <p className="text-hig-text-secondary whitespace-pre-line">{t('privacy_policy.controller.entity')}</p>
                            <p className="text-hig-text-secondary mt-2">
                                <a href="mailto:contact@humanreplica.ai" className="text-[#0A84FF] hover:underline">
                                    {t('privacy_policy.controller.email')}
                                </a>
                            </p>
                        </div>
                    </section>

                    {/* 3. What Data We Collect */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Database size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.data_collection.title')}
                        </h2>
                        <p className={`${body} mb-4`}>{t('privacy_policy.data_collection.description')}</p>
                        <p className={`${body} mb-2`}>{t('privacy_policy.data_collection.includes_intro')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(collectItems) && collectItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <div className="bg-hig-accent/[0.07] border border-hig-accent/20 rounded-xl p-4 mb-3">
                            <p className="text-hig-text-secondary text-sm">{t('privacy_policy.data_collection.note')}</p>
                        </div>
                        <div className="bg-hig-bg-tertiary/30 border border-hig-border rounded-xl p-4">
                            <p className="text-hig-text-secondary text-sm">
                                <strong className="text-hig-text-primary">reCAPTCHA:</strong>{' '}
                                {t('privacy_policy.data_collection.recaptcha_note', '')}
                            </p>
                        </div>
                    </section>

                    {/* 4. Purpose of Processing */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Eye size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.purpose.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.purpose.description')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(purposeItems) && purposeItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <div className="bg-hig-accent/[0.07] border border-hig-accent/20 rounded-xl p-4">
                            <p className="text-hig-text-secondary text-sm"><strong className="text-hig-accent">→</strong> {t('privacy_policy.purpose.no_sell')}</p>
                        </div>
                    </section>

                    {/* 5. Legal Basis (GDPR) */}
                    <section className={cardAccent}>
                        <h2 className={h2}>
                            <Scale size={20} className="text-hig-accent" />
                            {t('privacy_policy.legal_basis.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.legal_basis.description')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(legalBasisItems) && legalBasisItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    </section>

                    {/* 6. Data Sharing */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Users size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.data_sharing.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.data_sharing.description')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(sharingItems) && sharingItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <p className={`${body} mb-2`}>{t('privacy_policy.data_sharing.providers_note')}</p>
                        <p className={body}>{t('privacy_policy.data_sharing.legal_note')}</p>
                    </section>

                    {/* 7. International Data Transfers */}
                    <section className={cardAccent}>
                        <h2 className={h2}>
                            <Globe size={20} className="text-hig-accent" />
                            {t('privacy_policy.international_transfers.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.international_transfers.description')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(transferItems) && transferItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <div className="bg-hig-accent/[0.07] border border-hig-accent/20 rounded-xl p-4">
                            <p className="text-hig-text-secondary text-sm">{t('privacy_policy.international_transfers.note')}</p>
                        </div>
                    </section>

                    {/* 8. Data Retention */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Clock size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.data_retention.title')}
                        </h2>
                        <div className="bg-hig-accent/[0.07] border border-hig-accent/20 rounded-xl p-4 mb-4">
                            <p className="text-hig-text-secondary">{t('privacy_policy.data_retention.description')}</p>
                        </div>
                        <p className={body}>{t('privacy_policy.data_retention.transfer_note')}</p>
                    </section>

                    {/* 9. Your Rights (GDPR) */}
                    <section className={cardAccent}>
                        <h2 className={h2}>
                            <UserCheck size={20} className="text-hig-accent" />
                            {t('privacy_policy.your_rights.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.your_rights.description')}</p>
                        <ul className={`${listClass} mb-4`}>
                            {Array.isArray(rightsItems) && rightsItems.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                        <p className={body}>{t('privacy_policy.your_rights.contact_note')}</p>
                    </section>

                    {/* 10. Cookies */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Cookie size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.cookies.title')}
                        </h2>
                        <div className="bg-hig-accent/[0.07] border border-hig-accent/20 rounded-xl p-4 mb-4">
                            <p className="text-hig-text-secondary text-sm">{t('privacy_policy.cookies.p1')}</p>
                        </div>
                        <p className={`${body} mb-3`}>{t('privacy_policy.cookies.p2')}</p>
                        <p className={body}>{t('privacy_policy.cookies.p3', '')}</p>
                    </section>

                    {/* 11. Security */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Lock size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.security.title')}
                        </h2>
                        <p className={body}>{t('privacy_policy.security.description')}</p>
                    </section>

                    {/* 12. Changes to This Notice */}
                    <section className={card}>
                        <h2 className={h2}>
                            <RefreshCw size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.updates.title')}
                        </h2>
                        <p className={`${body} mb-2`}>{t('privacy_policy.updates.description')}</p>
                        <p className={body}>{t('privacy_policy.updates.posted_note')}</p>
                    </section>

                    {/* 13. Contact */}
                    <section className={card}>
                        <h2 className={h2}>
                            <Mail size={20} className="text-[#0A84FF]" />
                            {t('privacy_policy.contact.title')}
                        </h2>
                        <p className={`${body} mb-4`}>{t('privacy_policy.contact.description')}</p>
                        <div className={inner}>
                            <p className="text-hig-text-secondary">
                                <a href="mailto:contact@humanreplica.ai" className="text-[#0A84FF] hover:underline">
                                    contact@humanreplica.ai
                                </a>
                            </p>
                        </div>
                        <p className={`${body} mt-4 text-sm italic`}>{t('privacy_policy.contact.dpo_note', '')}</p>
                    </section>

                </div>
            </div>
        </MarketingShell>
    );
};

export default PrivacyPolicy;
